import { useEffect, useRef, useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { Button } from '../primitives/Button'
import { buildInfo, formatBuildDate, type BuildInfo } from './BuildInfo'
import { RELEASE_NOTES, formatReleaseDate, type ReleaseNote } from './release-notes'

export interface WhatsNewDialogProps {
  onClose: () => void
  /** Overridable for tests and for the visual QA screens. */
  notes?: ReleaseNote[]
  info?: BuildInfo
}

/**
 * „Was ist neu" — die Änderungen der aktuellen Version. Liegt über der ganzen
 * Oberfläche (der Aufgabenbereich ist schmal, ein Dialog am Rand würde nichts
 * bringen) und schließt per Schaltfläche, Escape oder Klick auf den Hintergrund.
 */
export function WhatsNewDialog({
  onClose,
  notes = RELEASE_NOTES,
  info = buildInfo,
}: WhatsNewDialogProps) {
  const [current, ...earlier] = notes
  const [showEarlier, setShowEarlier] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)

  // Escape schließt. Der Fokus wandert auf „Schließen", damit die Tastatur im
  // Dialog landet und nicht hinter ihm.
  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!current) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={onClose}
      data-testid="whats-new-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-title"
        // Der Klick im Fenster darf nicht am Hintergrund landen und schließen.
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        data-testid="whats-new"
      >
        <div className="flex items-start gap-3 border-b border-neutral-100 px-4 py-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="whats-new-title" className="text-base font-bold text-neutral-900">
              Was ist neu
            </h2>
            <p className="text-xs text-neutral-400">
              Version {current.version} · {formatReleaseDate(current.date)}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <Changes note={current} />

          {earlier.length > 0 && !showEarlier && (
            <button
              type="button"
              onClick={() => setShowEarlier(true)}
              className="mt-4 text-xs font-medium text-brand-600 hover:underline"
            >
              Frühere Versionen anzeigen
            </button>
          )}
          {showEarlier &&
            earlier.map((note) => (
              <div key={note.version} className="mt-5 border-t border-neutral-100 pt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
                  Version {note.version} · {formatReleaseDate(note.date)}
                </div>
                <Changes note={note} />
              </div>
            ))}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3">
          <span className="truncate text-[10px] text-neutral-400">
            Build {formatBuildDate(info.buildDate)}
          </span>
          <Button variant="primary" size="sm" onClick={onClose}>
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  )
}

function Changes({ note }: { note: ReleaseNote }) {
  return (
    <ul className="space-y-2">
      {note.changes.map((change) => (
        <li key={change} className="flex gap-2 text-sm leading-snug text-neutral-700">
          <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span>{change}</span>
        </li>
      ))}
    </ul>
  )
}
